/**
 * Utility functions for handling IDs in JavaScript
 */

/**
 * Maximum safe integer value in JavaScript (2^53 - 1)
 */
export const MAX_SAFE_INTEGER = Number.MAX_SAFE_INTEGER; // 9007199254740991

/**
 * Safely convert any ID value to a string to preserve precision
 * 
 * @param {any} id - The ID value to format
 * @returns {string|null} - The formatted ID as a string, or null if invalid
 */
export function formatId(id) {
  if (id === null || id === undefined) return null;
  
  // Handle arrays of IDs
  if (Array.isArray(id)) {
    return id.map(formatId).filter(Boolean);
  }
  
  // If already a string, validate it's a numeric string
  if (typeof id === 'string') {
    id = id.trim();
    return id !== '' && /^\d+$/.test(id) ? id : null;
  }
  
  // If it's a number, convert to string to preserve precision
  if (typeof id === 'number') {
    return String(id);
  }
  
  return null;
}

/**
 * Checks if an ID is within JavaScript's safe integer range
 * 
 * @param {number|string} id - The ID to check
 * @returns {boolean} - True if the ID is safe to use as a number
 */
export function isIdSafe(id) {
  if (id === null || id === undefined) return false;
  
  if (typeof id === 'string') {
    // Parse as number and check
    try {
      const numId = Number(id);
      return !isNaN(numId) && Number.isSafeInteger(numId);
    } catch (e) {
      return false;
    }
  }
  
  if (typeof id === 'number') {
    return Number.isSafeInteger(id);
  }
  
  return false;
}

/**
 * Process an object to convert all ID fields to strings
 * 
 * @param {Object} obj - The object to process
 * @returns {Object} - A new object with ID fields converted to strings
 */
export function processObjectIds(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  
  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => processObjectIds(item));
  }
  
  // Process object
  const result = {};
  for (const key in obj) {
    const value = obj[key];
    
    // Convert ID fields to strings
    if ((key === 'id' || key.endsWith('Id')) && 
        (typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value)))) {
      result[key] = formatId(value);
    }
    // Process nested objects
    else if (value && typeof value === 'object') {
      result[key] = processObjectIds(value);
    }
    // Copy other fields as-is
    else {
      result[key] = value;
    }
  }
  
  return result;
}

/**
 * Compare two ID values for equality, handling string/number conversions
 * 
 * @param {string|number} id1 - First ID
 * @param {string|number} id2 - Second ID
 * @returns {boolean} - True if the IDs are equal
 */
export function areIdsEqual(id1, id2) {
  // Convert both to formatted strings
  const strId1 = formatId(id1);
  const strId2 = formatId(id2);
  
  // Compare the string representations
  return strId1 === strId2;
}

/**
 * Install as a Vue plugin to add ID utilities to all components
 * 
 * @param {Vue} Vue - The Vue instance
 */
export function installIdUtils(Vue) {
  // Add a global mixin with ID utilities
  Vue.mixin({
    methods: {
      $formatId: formatId,
      $isIdSafe: isIdSafe,
      $areIdsEqual: areIdsEqual,
      $processObjectIds: processObjectIds
    }
  });
  
  // Add a global filter for displaying IDs
  Vue.filter('formatId', formatId);
}

/**
 * Find all large ID values in an object that exceed JavaScript's safe integer limit
 * Useful for debugging ID-related issues
 * 
 * @param {Object} obj - The object to scan for large IDs
 * @returns {Object} - An object mapping paths to large ID values
 */
export function findLargeIds(obj) {
  const results = {};
  
  function scan(object, path = '') {
    if (!object || typeof object !== 'object') return;
    
    if (Array.isArray(object)) {
      object.forEach((item, index) => {
        scan(item, path ? `${path}[${index}]` : `[${index}]`);
      });
      return;
    }
    
    for (const key in object) {
      const value = object[key];
      const currentPath = path ? `${path}.${key}` : key;
      
      // Check if this is an ID field with a large value
      if ((key === 'id' || key.endsWith('Id')) && 
          ((typeof value === 'number' && !Number.isSafeInteger(value)) ||
           (typeof value === 'string' && /^\d+$/.test(value) && !Number.isSafeInteger(Number(value))))) {
        results[currentPath] = value;
      } 
      
      // Recursively scan nested objects
      if (value && typeof value === 'object') {
        scan(value, currentPath);
      }
    }
  }
  
  scan(obj);
  return results;
}

/**
 * Enable logging of large ID values in API responses
 * This attaches a global interceptor to axios to detect and log large IDs
 * 
 * @param {Object} axiosInstance - The axios instance to monitor (defaults to global axios)
 */
export function enableLargeIdLogging(axiosInstance = null) {
  const axios = axiosInstance || require('axios');
  
  // Add response interceptor
  axios.interceptors.response.use(response => {
    // Check for large IDs in the response data
    const largeIds = findLargeIds(response.data);
    
    // If large IDs were found, log them
    if (Object.keys(largeIds).length > 0) {
      console.warn('⚠️ Large ID values detected in API response that exceed JavaScript safe integer limits:');
      console.warn('Request URL:', response.config.url);
      console.warn('Large IDs:', largeIds);
      
      // If you want to see the entire response for debugging
    }
    
    return response;
  }, error => {
    return Promise.reject(error);
  });
  
  console.log('Large ID logging enabled for API responses');
} 


// WEBPACK FOOTER //
// ./src/utils/id-utils.js